import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { OrganizationService } from '../../../shared/services/organization.service';
import { contractOutput } from '#backend/contract';
import { UserService } from '../../services/user.service';
import { EnhancedLoadingService } from '../../services/enhanced-loading.service';

type Organization = contractOutput['organization']['list']['items'][number];

export interface MonitoringFilterParams {
  organizationId?: number | null;
  organizationName?: string;
  safra?: string;
  cultura?: {
    id: number;
    description: string;
    enable: boolean;
  };
}

@Component({
  selector: 'app-monitoring-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './monitoring-filter.component.html',
  styleUrl: './monitoring-filter.component.scss',
})
export class MonitoringFilterComponent implements OnInit {
  private readonly smartClient = inject(SmartOrpcClientService);
  readonly loadingService = inject(EnhancedLoadingService);
  private readonly organizationService = inject(OrganizationService);
  private readonly userService = inject(UserService);
  @Output() filterChange = new EventEmitter<MonitoringFilterParams>();

  protected cropPeriods: string[] = [];
  protected cropCultures: {
    id: number;
    description: string;
    enable: boolean;
  }[] = [];
  protected organizations: Organization[] = [];
  protected filteredOrganizations: Organization[] = [];

  form = new FormGroup({
    organization: new FormControl<Organization | string | null>(null),
    safra: new FormControl(''),
    cultura: new FormControl<
      | {
          id: number;
          description: string;
          enable: boolean;
        }
      | string
      | null
    >(''),
  });

  ngOnInit(): void {
    this.initializeCropPeriods();

    this.form.disable();
    const promises = [this.getCropCulturesData(), this.loadOrganizations()];
    Promise.all(promises).finally(() => {
      this.form.enable();
    });

    this.setupOrganizationFilter();
  }

  private initializeCropPeriods(): void {
    const now = new Date();
    this.cropPeriods = [
      `${now.getFullYear() - 1}/${now.getFullYear()}`,
      `${now.getFullYear()}/${now.getFullYear()}`,
      `${now.getFullYear()}/${now.getFullYear() + 1}`,
      `${now.getFullYear() + 1}/${now.getFullYear() + 1}`,
    ];
  }

  private async loadOrganizations(): Promise<void> {
    try {
      if (!this.userService.isGeoambiente) return;
      this.organizations = await this.organizationService.getOrganizations(
        false,
        true,
      );
      this.filteredOrganizations = this.organizations;
    } catch (error) {
      console.error('Erro ao carregar organizações:', error);
    }
  }

  private setupOrganizationFilter(): void {
    this.form.controls.organization.valueChanges.subscribe((value) => {
      if (typeof value === 'string') {
        this.filterOrganizations(value);
      }
    });
  }

  private filterOrganizations(searchTerm: string): void {
    if (!searchTerm) {
      this.filteredOrganizations = this.organizations;
      return;
    }

    const term = searchTerm.toLowerCase();
    this.filteredOrganizations = this.organizations.filter((org) =>
      org.name.toLowerCase().includes(term),
    );
  }

  protected displayOrganizationName(
    organization: Organization | string | null,
  ): string {
    if (!organization) return '';
    if (typeof organization === 'string') {
      // Trata valores especiais
      if (organization === '__ALL__') return 'Todos';
      if (organization === 'Geoambiente') return 'Geoambiente';
      return organization;
    }
    return organization?.name || '';
  }

  private async getCropCulturesData(): Promise<void> {
    try {
      const cropCultures =
        await this.smartClient.client.monitoring.cultures.list({});
      this.cropCultures = cropCultures;

      if (this.form.controls.cultura.value === null) {
        setTimeout(() => {
          this.form.controls.cultura.setValue(null);
        }, 100);
      }
    } catch (error) {
      console.error('Erro getCulturesData():', error);
    }
  }

  onApplyFilter(): void {
    const selectedOrg = this.form.value.organization as
      Organization | string | null;

    let organizationIdValue: number | null | undefined;

    if (selectedOrg && typeof selectedOrg === 'object') {
      // É um objeto Organization - envia o ID
      organizationIdValue = selectedOrg.id;
    } else if (typeof selectedOrg === 'string') {
      // Trata valores especiais
      if (selectedOrg === '__ALL__') {
        // Não adiciona filtro de organização para buscar todos
        organizationIdValue = undefined;
      } else if (selectedOrg === 'Geoambiente') {
        // Define como null para buscar usuários sem organização
        organizationIdValue = null;
      }
    } else {
      // Não selecionado
      organizationIdValue = undefined;
    }

    const filterParams: MonitoringFilterParams = {
      organizationId: organizationIdValue,
      organizationName:
        typeof selectedOrg === 'object' ? selectedOrg?.name : undefined,
      safra: this.form.value.safra || undefined,
      cultura:
        typeof this.form.value.cultura === 'object' &&
        this.form.value.cultura !== null
          ? this.form.value.cultura
          : undefined,
    };

    this.filterChange.emit(filterParams);
  }

  clearOrganizationField(): void {
    this.form.controls.organization.reset();
    this.filterOrganizations('');
  }

  onClearFilter(): void {
    this.form.reset();
    this.filterChange.emit({});
  }
}
